import React, { memo } from "react";
import styles from "./BaseTable.module.scss";
import classNames from "classnames/bind";
import { ColumnType } from "~/utils/types/common";

type BaseTableProps<T extends { id: string }> = {
  data: Array<T>;
  columns: Array<ColumnType>;
  page?: number;
  totalPages?: number;
  onChangePage?: (page: number) => void;
  onRowClick?: (item: T) => void;
  showPagination?: boolean;
  className?: string;
};

const cx = classNames.bind(styles);

const BaseTable = <T extends { id: string }>(props: BaseTableProps<T>) => {
  const {
    data,
    columns,
    onChangePage,
    totalPages,
    page,
    onRowClick,
    showPagination,
    className,
  } = props;

  const handleChangePage = (event: unknown, newPage: number) => {
    if (onChangePage) {
      onChangePage(newPage);
    }
  };

  const handleRowClick = (item: T) => {
    if (onRowClick) {
      onRowClick(item);
    }
  };

  return (
    <div className={`${cx("container")} ${className} container-custom`}>
      <div className={`${cx("table-wrapper")} table-wrapper-custom`}>
        <div className={cx("table-inner")}>
          <div className={cx("table-head")}>
            {columns.map((column, index) => (
              <div
                key={column.id as string}
                className={cx("table-head-cell")}
                style={{
                  width: column.width,
                  minWidth: column.minWidth,
                  flex: column.width ? `0 0 ${column.width}` : "1",
                }}
              >
                {column.labelHeader ?? column.id}
              </div>
            ))}
          </div>
          <div className={cx("table-body")}>
            {data.length > 0 ? (
              <>
                {data.map((item: T, index: number) => {
                  return (
                    <div
                      key={item.id}
                      className={cx(
                        "table-body-row",
                        onRowClick ? "clickable" : ""
                      )}
                      onClick={() => handleRowClick(item)}
                    >
                      {columns.map((column, index) => (
                        <div
                          key={column.id as string}
                          className={cx("table-body-cell")}
                          style={{
                            width: column.width,
                            minWidth: column.minWidth,
                            flex: column.width ? `0 0 ${column.width}` : "1",
                          }}
                        >
                          {column.renderCell?.(item, index)}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </>
            ) : (
              <div className={cx("no-items-container")}>
                <h3 className={cx("no-items-text")}>No items</h3>
              </div>
            )}
          </div>
        </div>
      </div>
      {showPagination && (
        <div className={cx("pagination")}>
          <p className={cx("pagination-text")}>
            Page {page} of {totalPages}
          </p>
          <div className={cx("pagination-buttons")}>
            <button
              className={cx(
                "pagination-button",
                "previous",
                (page === 1 || page === 0) && "disabled"
              )}
              onClick={() => handleChangePage(null, page ? page - 1 : 0)}
              disabled={page === 1 || page === 0}
            >
              Previous
            </button>
            <button
              className={cx(
                "pagination-button",
                "next",
                page === totalPages && "disabled"
              )}
              onClick={() => handleChangePage(null, page ? page + 1 : 0)}
              disabled={page === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(BaseTable) as typeof BaseTable;
